import ApiERP from '@/core/conectores/ApiERP';
import { IFiltroGenerico } from '@/core/models/BuscaAvancada/IFiltroGenerico';
import { IItemConsultaRapida } from '@/core/models/Consulta/IItemConsultaRapida';
import { IListaPaginada } from '@/core/models/Consulta/IListaPaginada';
import { IParametrosConsulta } from '@/core/models/Consulta/IParametrosConsulta';
import { IParametrosConsultaRapida } from '@/core/models/Consulta/IParametrosConsultaRapida';
import { IPropriedadeConsulta } from '@/core/models/Consulta/PropriedadeConsulta';
import { ETipoArquivo } from '@/core/models/Enumeradores/ETipoArquivo';
import { IRetornoRelatorio } from '@/core/models/IRetornoRelatorio';
import { IRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import { IServicoBase } from '@/core/models/IServicoBase';
import { ICipEstrutura } from '@/models/Entidades/Cadastros/PCP/ICipEstrutura';

class ServicoCipEstrutura implements IServicoBase<ICipEstrutura> {
  endPoint = 'cip-estrutura';

  apiERP = new ApiERP();

  requisicaoSistema(): void {
    this.apiERP = new ApiERP(true);
  }

  public async obterPropriedadesConsulta(): Promise<IPropriedadeConsulta[]> {
    // O backend atual de CIP ainda nao expoe /propriedades-consulta.
    // Retornar vazio evita 404 no interceptor global e mantem a tela funcional.
    return [];
  }

  public async buscaAvancada(parametrosConsulta: IParametrosConsulta, filtros?: Array<IFiltroGenerico>): Promise<IListaPaginada> {
    if (parametrosConsulta.codigosSelecionados !== undefined && parametrosConsulta.codigosSelecionados.length > 0) {
      const listaPorCodigo = await Promise.all(parametrosConsulta.codigosSelecionados.map(async (codigo) => this.obterPorCodigo(codigo)));
      const listaFiltrada = listaPorCodigo.filter((item) => item !== undefined && item !== null);
      return this.montaListaPaginada(listaFiltrada, 1, parametrosConsulta.qtdeRegistrosPagina ?? 10);
    }

    const codigoTipoProducao = this.obterCodigoTipoProducao(filtros);

    if (codigoTipoProducao <= 0 && parametrosConsulta.valorBuscaRapida !== undefined && parametrosConsulta.valorBuscaRapida !== '') {
      const parametrosConsultaRapida: IParametrosConsultaRapida = {
        valor: parametrosConsulta.valorBuscaRapida,
        apenasAtivos: true,
        quantidadeRegistros: 100,
        recursoAssociado: '',
        empresas: parametrosConsulta.empresas,
      };

      const itensConsulta = await this.consultaRapida(parametrosConsultaRapida);
      if (itensConsulta.length === 0) {
        return this.montaListaPaginada([], 1, parametrosConsulta.qtdeRegistrosPagina ?? 10);
      }

      const listaConsultaRapida = await Promise.all(itensConsulta.map(async (item) => this.obterPorCodigo(item.codigo)));
      const listaFiltrada = listaConsultaRapida.filter((item) => item !== undefined && item !== null);

      return this.montaListaPaginada(
        listaFiltrada,
        parametrosConsulta.numeroPagina ?? 1,
        parametrosConsulta.qtdeRegistrosPagina ?? 10,
      );
    }

    if (codigoTipoProducao <= 0) {
      const parametrosConsultaRapida: IParametrosConsultaRapida = {
        valor: parametrosConsulta.valorBuscaRapida ?? '',
        apenasAtivos: true,
        quantidadeRegistros: 200,
        recursoAssociado: '',
        empresas: parametrosConsulta.empresas,
      };

      const itensConsulta = await this.consultaRapida(parametrosConsultaRapida);
      if (itensConsulta.length === 0) {
        return this.montaListaPaginada([], 1, parametrosConsulta.qtdeRegistrosPagina ?? 10);
      }

      const listaConsultaRapida = await Promise.all(itensConsulta.map(async (item) => this.obterPorCodigo(item.codigo)));
      const listaFiltrada = listaConsultaRapida.filter((item) => item !== undefined && item !== null);

      return this.montaListaPaginada(
        listaFiltrada,
        parametrosConsulta.numeroPagina ?? 1,
        parametrosConsulta.qtdeRegistrosPagina ?? 10,
      );
    }

    let lista = await this.listarPorTipoProducao(codigoTipoProducao);

    if (parametrosConsulta.valorBuscaRapida !== undefined && parametrosConsulta.valorBuscaRapida !== '') {
      const termo = parametrosConsulta.valorBuscaRapida.toLowerCase();
      lista = lista.filter((item) => `${item.codigo}`.includes(termo)
        || (item.descricao !== undefined && item.descricao.toLowerCase().includes(termo)));
    }

    const paginaAtual = parametrosConsulta.numeroPagina ?? 1;
    const qtdeRegistrosPagina = parametrosConsulta.qtdeRegistrosPagina ?? 10;

    return this.montaListaPaginada(lista, paginaAtual, qtdeRegistrosPagina);
  }

  public async relatorioPadrao(tipoArquivo: ETipoArquivo, parametrosConsulta: IParametrosConsulta, filtros?: IFiltroGenerico[], codigosSelecionados?: Array<number>): Promise<IRetornoRelatorio> {
    return this.apiERP.relatorioPadrao(this.endPoint, tipoArquivo, parametrosConsulta, filtros, codigosSelecionados);
  }

  public async imprimirEtiquetaPadrao(codigoRegistro: number, empresa: number): Promise<IRetornoRelatorio> {
    const result: any = await this.apiERP.get(`${this.endPoint}/etiqueta/${codigoRegistro}/empresa/${empresa}`);
    return result.data;
  }

  public obterIdentificacaoRegistroGradeCRUD(objeto: ICipEstrutura): string {
    return objeto.descricao;
  }

  public async consultaRapida(parametrosConsultaRapida: IParametrosConsultaRapida): Promise<IItemConsultaRapida[]> {
    const parametrosAdicionais = this.apiERP.obterParametrosAdicionaisConsultaRapida(parametrosConsultaRapida);
    const result: any = await this.apiERP.get(`${this.endPoint}/consulta-rapida${parametrosAdicionais}`);
    return result.data;
  }

  public async incluir(objeto: ICipEstrutura): Promise<IRetornoRequisicao> {
    const result: any = await this.apiERP.post(`${this.endPoint}`, objeto);
    return result.data;
  }

  public async alterar(objeto: ICipEstrutura): Promise<IRetornoRequisicao> {
    const result: any = await this.apiERP.put(`${this.endPoint}`, objeto);
    return result.data;
  }

  public async obter(codigo: number, empresa: number): Promise<ICipEstrutura> {
    const result: any = await this.apiERP.get(`${this.endPoint}/${codigo}${empresa > 0 ? '' : ''}`);
    return result.data;
  }

  public async obterPorCodigo(codigo: number): Promise<ICipEstrutura> {
    const result: any = await this.apiERP.get(`${this.endPoint}/${codigo}`);
    return result.data;
  }

  public async listarPorTipoProducao(codigoTipoProducao: number): Promise<ICipEstrutura[]> {
    const result: any = await this.apiERP.get(`${this.endPoint}/tipo-producao/${codigoTipoProducao}`);
    return result.data;
  }

  public async excluir(codigo: number): Promise<IRetornoRequisicao> {
    const result: any = await this.apiERP.delete(`${this.endPoint}/${codigo}`);
    return result.data;
  }

  private obterCodigoTipoProducao(filtros?: Array<IFiltroGenerico>): number {
    if (filtros === undefined || filtros.length === 0) {
      return 0;
    }

    const filtroTipoProducao = filtros.find((f) => f.identificador.toLowerCase() === 'codigotipoproducao');
    if (filtroTipoProducao === undefined || filtroTipoProducao.valores.length === 0) {
      return 0;
    }

    const codigo = Number(filtroTipoProducao.valores[0]);
    return Number.isNaN(codigo) ? 0 : codigo;
  }

  private montaListaPaginada(lista: ICipEstrutura[], paginaAtual: number, qtdeRegistrosPagina: number): IListaPaginada {
    const totalRegistros = lista.length;
    const qtdePorPagina = qtdeRegistrosPagina > 0 ? qtdeRegistrosPagina : 10;
    const totalPaginas = totalRegistros === 0 ? 1 : Math.ceil(totalRegistros / qtdePorPagina);
    const pagina = paginaAtual > 0 ? paginaAtual : 1;
    const inicio = (pagina - 1) * qtdePorPagina;
    const fim = inicio + qtdePorPagina;

    return {
      dados: lista.slice(inicio, fim),
      metaData: {
        totalRegistros,
        qtdeRegistrosPagina: qtdePorPagina,
        paginaAtual: pagina,
        totalPaginas,
        possuiProximaPagina: pagina < totalPaginas,
        possuiPaginaAnterior: pagina > 1,
      },
    };
  }
}

export default ServicoCipEstrutura;
